import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useObs } from '../contexts/ObsContext';
import { Spinner } from '../components/ui';
import { obsAPI } from '../api';
import { resumeObservation } from '../lib/resumeObservation';

/** Observations that have been completed but not yet debriefed. */
const PENDING_STATUSES = 'obs_complete,debrief_pending';

const dayLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d)) return '';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((today - d) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days > 1 && days < 7) return d.toLocaleDateString(undefined, { weekday: 'short' });
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

export default function PendingDebriefsPage() {
  const navigate = useNavigate();
  const { setObs, setTeacher, resetObs } = useObs();

  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    let cancelled = false;
    obsAPI.list({ status: PENDING_STATUSES })
      .then(({ data }) => {
        if (!cancelled) setItems(data.results ?? data);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError('Could not load pending debriefs. Please try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const open = (item) => {
    const route = resumeObservation(item, { setObs, setTeacher, resetObs });
    navigate(route);
  };

  return (
    <div className="screen">
      <div className="obsbar">
        <button className="obsbar-back" onClick={() => navigate('/')}>← Back</button>
        <div className="obsbar-title">Debriefs pending</div>
      </div>

      <div className="scroll">
        <div className="pad">
          {loading && (
            <div style={{ display:'flex', justifyContent:'center', padding:'48px 0' }}>
              <Spinner />
            </div>
          )}

          {!loading && error && <div className="dash-empty">{error}</div>}

          {!loading && !error && items.length === 0 && (
            <div className="dash-empty">
              No debriefs are waiting. Every observation you have completed has been
              debriefed and sent.
            </div>
          )}

          {!loading && !error && items.length > 0 && (
            <>
              <div className="pend-lead">
                {items.length} observation{items.length === 1 ? '' : 's'} waiting to be
                debriefed. Opening one rebuilds its summary from the notes you saved.
              </div>

              {items.map(item => (
                <button key={item.id} className="pend-item" onClick={() => open(item)}>
                  <span className="pend-av">{item.teacher_initials || '—'}</span>
                  <span className="pend-body">
                    <span className="pend-name">
                      {item.teacher_display_name || item.teacher_name || 'Unknown teacher'}
                    </span>
                    <span className="pend-sub">
                      {[item.school_name, item.grade_level, item.subject].filter(Boolean).join(' · ')}
                    </span>
                    <span className="pend-meta">
                      {item.note_count} evidence {item.note_count === 1 ? 'note' : 'notes'}
                      {item.status === 'obs_complete' && ' · not yet reviewed'}
                    </span>
                  </span>
                  <span className="pend-right">
                    <span className="pend-day">{dayLabel(item.observation_date)}</span>
                    <span className="pend-chev">›</span>
                  </span>
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
